import {
  ArrowRight,
  Clock3,
  CreditCard,
  DollarSign,
  Filter,
  Search,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { createAdminPayment, getAdminAgreements, getAdminPayment, getAdminPayments, getAdminProposals, updateAdminPayment } from "../../lib/api";
import type { AdminAgreementRecord, AdminPaymentDetail, AdminPaymentRecord, AdminProposalRecord } from "../../lib/types";
import { FormPortalHeader } from "./FormPortalHeader";

const paymentStatuses = ["All", "Draft", "Sent", "Partial", "Paid", "Overdue", "Cancelled"] as const;

export function PaymentRecordsPage() {
  const { paymentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [payments, setPayments] = useState<AdminPaymentRecord[]>([]);
  const [proposals, setProposals] = useState<AdminProposalRecord[]>([]);
  const [agreements, setAgreements] = useState<AdminAgreementRecord[]>([]);
  const [detail, setDetail] = useState<AdminPaymentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(Boolean(paymentId));
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [busyAction, setBusyAction] = useState(false);
  const [createProposalId, setCreateProposalId] = useState("");
  const [createAgreementId, setCreateAgreementId] = useState("");
  const [paymentMode, setPaymentMode] = useState<"deposit" | "in_full" | "custom">("deposit");
  const [dueDate, setDueDate] = useState("");
  const [finalDueDate, setFinalDueDate] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<(typeof paymentStatuses)[number]>("All");

  useEffect(() => {
    document.title = paymentId ? "Payment Detail | Business Center OS" : "Payment Records | Business Center OS";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Shared Business Center OS payment workspace for invoices, deposits, balances, and paid payment histories.",
      );
    void loadPayments();
  }, [paymentId]);

  useEffect(() => {
    if (!paymentId) {
      setDetail(null);
      return;
    }
    void loadDetail(paymentId);
  }, [paymentId]);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash, detail, payments]);

  async function loadPayments() {
    setLoading(true);
    setError("");
    try {
      const [paymentData, proposalData, agreementData] = await Promise.all([
        getAdminPayments(),
        getAdminProposals(),
        getAdminAgreements(),
      ]);
      setPayments(paymentData.payments || []);
      setProposals(proposalData.proposals || paymentData.proposals || []);
      setAgreements(agreementData.agreements || []);
      setCreateProposalId((current) => current || proposalData.proposals?.[0]?.id || paymentData.proposals?.[0]?.id || "");
      setCreateAgreementId((current) => current || agreementData.agreements?.[0]?.id || "");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load payment records.");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(targetPaymentId: string) {
    setLoadingDetail(true);
    setError("");
    try {
      const data = await getAdminPayment(targetPaymentId);
      setDetail(data);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load payment detail.");
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCreatePayment() {
    if (!createProposalId) {
      setActionMessage("Select a proposal to create an invoice.");
      return;
    }
    setBusyAction(true);
    setActionMessage("");
    try {
      const result = await createAdminPayment({
        proposalId: createProposalId,
        agreementId: createAgreementId || null,
        mode: paymentMode,
        dueDate: dueDate || null,
        finalDueDate: finalDueDate || null,
      });
      setActionMessage(`Created ${result.payment.invoiceNumber} and opened the detail view.`);
      await loadPayments();
      navigate(`/admin/payment-records/${result.payment.id}`);
    } catch (createError) {
      setActionMessage(createError instanceof Error ? createError.message : "Unable to create payment.");
    } finally {
      setBusyAction(false);
    }
  }

  async function updatePaymentStatus(action: "invoice_sent" | "partial_received" | "paid_in_full" | "failed" | "refunded") {
    if (!detail) return;
    setBusyAction(true);
    setActionMessage("");
    try {
      const result = await updateAdminPayment(detail.payment.id, {
        action,
        paidCents: action === "partial_received" ? Math.max(1, Math.round((detail.payment.amountRemainingCents || detail.payment.totalCents) / 2)) : undefined,
        refundedCents: action === "refunded" ? detail.payment.amountPaidCents || detail.payment.totalCents : undefined,
        reason: action === "failed" ? "Marked failed by admin." : undefined,
      });
      setActionMessage(`Payment updated to ${action.replaceAll("_", " ")}.`);
      await loadPayments();
      await loadDetail(detail.payment.id);
      if (result.payment?.id) {
        navigate(`/admin/payment-records/${result.payment.id}`);
      }
    } catch (updateError) {
      setActionMessage(updateError instanceof Error ? updateError.message : "Unable to update payment status.");
    } finally {
      setBusyAction(false);
    }
  }

  const filteredPayments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return payments.filter((payment) => {
      const matchesStatus = statusFilter === "All" || payment.displayStatus === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [
          payment.invoiceNumber,
          payment.id,
          payment.leadName,
          payment.leadEmail,
          payment.eventName,
          payment.proposalNumber,
          payment.stripeInvoiceId || "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [payments, query, statusFilter]);

  const metrics = useMemo(() => {
    const revenueCollected = payments.reduce((sum, payment) => sum + Math.max(0, payment.amountPaidCents || 0), 0);
    const outstandingBalances = payments.reduce((sum, payment) => sum + Math.max(0, payment.amountRemainingCents || 0), 0);
    const depositsReceived = payments.reduce((sum, payment) => (payment.paymentMode === "deposit" && payment.amountPaidCents > 0 ? sum + payment.amountPaidCents : sum), 0);
    const upcomingPayments = payments.filter((payment) => payment.displayStatus === "Sent" && Boolean(payment.dueDate)).length;
    const overduePayments = payments.filter((payment) => payment.displayStatus === "Overdue").length;
    return [
      { icon: DollarSign, label: "Revenue Collected", value: revenueCollected },
      { icon: TrendingDown, label: "Outstanding Balances", value: outstandingBalances },
      { icon: CreditCard, label: "Deposits Received", value: depositsReceived },
      { icon: Clock3, label: "Upcoming Payments", value: upcomingPayments },
      { icon: ShieldCheck, label: "Overdue Payments", value: overduePayments },
      { icon: TrendingUp, label: "Paid Clients", value: payments.filter((payment) => payment.displayStatus === "Paid").length },
    ];
  }, [payments]);

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader
        brandHref="/admin/business-center-os"
        brandName="Business Center OS"
        brandSubtitle="Shared lifecycle records"
        brandLogoAlt="Business Center OS"
        adminDirectoryHref="/admin/business-center-os"
        adminDirectoryLabel="Back to Business Center OS"
        showPublicBackLink={false}
      />
      <Hero
        eyebrow="Business Center OS"
        title="Payment Records Workspace"
        description="Track invoices, deposits, balances, and payment history; search by client, invoice number, event, or email; and open the linked proposal or agreement from a shared platform surface."
      />

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <div id="payment-status" className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {metrics.map((metric) => (
              <MetricCard key={metric.label} icon={metric.icon} label={metric.label} value={metric.value} />
            ))}
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading eyebrow="Invoice Creation" title="Create a new payment record" />
            <p className="mt-3 max-w-3xl text-sm font-semibold leading-7 text-[#5c5246]">
              Select a proposal, optionally link an agreement, choose a payment plan, and generate the invoice record
              for deposits, full payment, or a custom schedule.
            </p>
            <div className="mt-5 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Proposal
                <select
                  value={createProposalId}
                  onChange={(event) => setCreateProposalId(event.target.value)}
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                >
                  <option value="">Select a proposal</option>
                  {proposals.map((proposal) => (
                    <option key={proposal.id} value={proposal.id}>
                      {proposal.proposalNumber} - {proposal.leadName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Agreement
                <select
                  value={createAgreementId}
                  onChange={(event) => setCreateAgreementId(event.target.value)}
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                >
                  <option value="">No agreement linked</option>
                  {agreements.map((agreement) => (
                    <option key={agreement.id} value={agreement.id}>
                      {agreement.agreementNumber} - {agreement.leadName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Payment Mode
                <select
                  value={paymentMode}
                  onChange={(event) => setPaymentMode(event.target.value as "deposit" | "in_full" | "custom")}
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                >
                  <option value="deposit">Deposit</option>
                  <option value="in_full">Full Payment</option>
                  <option value="custom">Custom Schedule</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Due Date
                <input
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  type="date"
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#050505]">
                Final Due Date
                <input
                  value={finalDueDate}
                  onChange={(event) => setFinalDueDate(event.target.value)}
                  type="date"
                  className="command-center-field focus-ring min-h-12 rounded-lg px-4 py-3 text-sm font-semibold"
                />
              </label>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => void handleCreatePayment()}
                  disabled={busyAction || !proposals.length}
                  className="admin-primary-button focus-ring min-h-12 w-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busyAction ? "Creating..." : "Create Invoice"}
                </button>
              </div>
            </div>
            {actionMessage ? <Notice tone="success" message={actionMessage} /> : null}
            {!proposals.length ? (
              <p className="mt-4 text-sm font-semibold leading-7 text-[#5c5246]">
                No proposals are available yet. Create a proposal first, then return here to generate the invoice
                record.
              </p>
            ) : null}
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-[#D94A16]/16 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Payment Search</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#050505]">Payment Table</h2>
                <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#5c5246]">
                  Search by client, invoice number, event, or email address.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={() => void loadPayments()} className="admin-secondary-button focus-ring" title="Refresh payment records">
                  Refresh
                </button>
                <Link to="/admin/business-center-os" className="admin-primary-button focus-ring" title="Back to Business Center OS">
                  Back to Business Center OS
                </Link>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_280px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
                  placeholder="Search client, invoice, event, email, or proposal"
                />
              </label>
              <label className="relative block">
                <Filter className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as (typeof paymentStatuses)[number])}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-bold"
                >
                  {paymentStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {error ? <Notice tone="error" message={error} /> : null}
            {loading ? <EmptyState title="Loading payments" message="Checking the invoice database for the latest payment records and balances." /> : null}
            {!loading && !error && !filteredPayments.length ? (
              <EmptyState
                title="No payment records yet"
                message="Your first invoice will appear here once an agreement moves into the payment workflow."
              />
            ) : null}

            <div className="mt-6 overflow-hidden rounded-lg border border-[#D94A16]/16">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#D94A16]/14 text-left">
                  <thead className="bg-black/20 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                    <tr>
                      <Th>Invoice Number</Th>
                      <Th>Client</Th>
                      <Th>Event</Th>
                      <Th>Invoice Amount</Th>
                      <Th>Deposit</Th>
                      <Th>Balance Due</Th>
                      <Th>Status</Th>
                      <Th>Due Date</Th>
                      <Th>Related Records</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D94A16]/10 bg-white/5">
                    {filteredPayments.map((payment) => (
                      <tr key={payment.id} className="transition hover:bg-white/8">
                        <Td className="font-black text-[#fff9ef]">{payment.invoiceNumber}</Td>
                        <Td>
                          <div className="grid">
                            <span className="font-bold text-[#fff9ef]">{payment.leadName}</span>
                            <span className="text-xs font-semibold text-[#fff9ef]/68">{payment.leadEmail || "No email"}</span>
                          </div>
                        </Td>
                        <Td className="text-[#fff9ef]/82">{payment.eventName || "Event not set"}</Td>
                        <Td>{formatMoney(payment.totalCents)}</Td>
                        <Td>{formatMoney(payment.depositCents)}</Td>
                        <Td>{formatMoney(payment.amountRemainingCents)}</Td>
                        <Td>
                          <StatusPill status={payment.displayStatus} />
                        </Td>
                        <Td>{formatDate(payment.dueDate)}</Td>
                        <Td className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            <Link
                              to={`/admin/proposals#proposal-records`}
                              className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black text-[#FF6A2B] transition hover:border-[#FF6A2B] hover:bg-[#FF6A2B]/12"
                            >
                              Proposal {payment.proposalNumber}
                            </Link>
                            <Link
                              to={payment.agreementId ? `/admin/agreement-records/${payment.agreementId}` : "/admin/agreement-records"}
                              className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black text-[#FF6A2B] transition hover:border-[#FF6A2B] hover:bg-[#FF6A2B]/12"
                            >
                              Agreement
                            </Link>
                          </div>
                          <Link
                            to={`/admin/payment-records/${payment.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black text-[#FF6A2B] transition hover:border-[#FF6A2B] hover:bg-[#FF6A2B]/12"
                          >
                            View detail
                            <ArrowRight className="size-3" aria-hidden="true" />
                          </Link>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {loadingDetail ? <EmptyState title="Loading payment detail" message="Opening the invoice detail page and loading payment history, linked records, and payment timeline." /> : null}

          {detail ? (
            <section className="grid gap-6">
              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Payment Detail</p>
                <h2 className="mt-2 font-display text-3xl font-bold text-[#050505]">{detail.payment.invoiceNumber || invoiceTitle(detail.payment.token)}</h2>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">
                  Review the invoice record, linked proposal and agreement, payment history, schedule items, and payment timeline.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void updatePaymentStatus("invoice_sent")}
                    disabled={busyAction}
                    className="admin-secondary-button focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Invoice Sent
                  </button>
                  <button
                    type="button"
                    onClick={() => void updatePaymentStatus("partial_received")}
                    disabled={busyAction}
                    className="admin-secondary-button focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Partial Payment Received
                  </button>
                  <button
                    type="button"
                    onClick={() => void updatePaymentStatus("paid_in_full")}
                    disabled={busyAction}
                    className="admin-primary-button focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Paid In Full
                  </button>
                  <button
                    type="button"
                    onClick={() => void updatePaymentStatus("failed")}
                    disabled={busyAction}
                    className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-5 py-3 text-sm font-extrabold text-rose-200 transition hover:bg-rose-500/18 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Failed
                  </button>
                  <button
                    type="button"
                    onClick={() => void updatePaymentStatus("refunded")}
                    disabled={busyAction}
                    className="rounded-lg border border-orange-400/30 bg-orange-500/10 px-5 py-3 text-sm font-extrabold text-orange-200 transition hover:bg-orange-500/18 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Refunded
                  </button>
                  {actionMessage ? <span className="self-center text-sm font-semibold text-[#D94A16]">{actionMessage}</span> : null}
                </div>
                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <InfoCard label="Client" value={detail.lead?.fullName || "Unknown"} />
                  <InfoCard label="Proposal" value={detail.proposal?.proposal.proposalNumber || "Not linked"} />
                  <InfoCard label="Agreement" value={detail.agreement?.agreementNumber || "Not linked"} />
                  <InfoCard label="Invoice Status" value={detail.payment.invoiceStatus || detail.payment.status} />
                </div>
              </article>

              <article id="payment-link-readiness" className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Invoice History" title="Invoice details and payment tracking" />
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <DetailBlock
                    title="Invoice Information"
                    items={[
                      ["Invoice Number", detail.payment.invoiceNumber || invoiceTitle(detail.payment.token)],
                      ["Payment Mode", detail.payment.paymentMode],
                      ["Invoice Status", detail.payment.invoiceStatus || detail.payment.status],
                      ["Sent / Created", formatDate(detail.payment.createdAt)],
                      ["Due Date", formatDate(detail.payment.dueDate)],
                      ["Final Due Date", formatDate(detail.payment.finalDueDate)],
                    ]}
                  />
                  <DetailBlock
                    title="Financial Summary"
                    items={[
                      ["Invoice Amount", formatMoney(detail.payment.totalCents)],
                      ["Deposit", formatMoney(detail.payment.depositCents)],
                      ["Paid", formatMoney(detail.payment.amountPaidCents)],
                      ["Balance Due", formatMoney(detail.payment.amountRemainingCents)],
                      ["Tax", detail.payment.taxLabel ? `${detail.payment.taxLabel} (${detail.payment.taxPercentage || 0}%)` : "Not applied"],
                      ["Currency", detail.payment.currency.toUpperCase()],
                    ]}
                  />
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Payment Timeline" title="Created, sent, deposit received, reminder, and paid in full" />
                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {buildPaymentTimeline(detail).map((item) => (
                    <TimelineCard key={item.label} label={item.label} value={item.value} active={item.active} />
                  ))}
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Schedule Items" title="Payment schedule and installment readiness" />
                <div className="mt-5 grid gap-3">
                  {detail.scheduleItems.length ? (
                    detail.scheduleItems.map((item) => (
                      <div key={item.id} className="rounded-lg border border-[#FF6A2B]/16 bg-white/5 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="font-black text-[#fff9ef]">{item.label}</p>
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{item.status}</p>
                        </div>
                        <div className="mt-2 grid gap-2 md:grid-cols-3">
                          <InfoCard label="Amount" value={formatMoney(item.amountCents)} />
                          <InfoCard label="Due Date" value={formatDate(item.dueDate)} />
                          <InfoCard label="Paid At" value={formatDate(item.paidAt)} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState title="No schedule items yet" message="Custom payment schedules will appear here when a payment plan is created." />
                  )}
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Payment Notes" title="Transactions and invoice activity" />
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border border-[#FF6A2B]/16 bg-white/5 p-4">
                    <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Payment transactions</p>
                    <div className="mt-4 grid gap-3">
                      {detail.transactions.length ? (
                        detail.transactions.map((transaction) => (
                          <div key={transaction.id} className="rounded-lg border border-[#D94A16]/16 bg-black/10 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <p className="font-black text-[#fff9ef]">{transaction.description || transaction.providerEventId || "Transaction"}</p>
                              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{transaction.status}</p>
                            </div>
                            <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">
                              {formatMoney(transaction.amountCents)} via {transaction.methodType} • {formatDate(transaction.createdAt)}
                            </p>
                          </div>
                        ))
                      ) : (
                        <EmptyState title="No transaction history yet" message="Payment provider events will appear here after deposit or invoice activity." />
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-[#FF6A2B]/16 bg-white/5 p-4">
                    <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Activity log</p>
                    <div className="mt-4 grid gap-3">
                      {detail.timeline.length ? (
                        detail.timeline.map((item) => (
                          <div key={item.id} className="rounded-lg border border-[#D94A16]/16 bg-black/10 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <p className="font-black text-[#fff9ef]">{item.activityType}</p>
                              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{formatDate(item.createdAt)}</p>
                            </div>
                            <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">{item.notes || "No notes recorded."}</p>
                          </div>
                        ))
                      ) : (
                        <EmptyState title="No payment activity yet" message="Invoice and payment events will appear here as the workspace is used." />
                      )}
                    </div>
                  </div>
                </div>
              </article>

              <article className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading eyebrow="Related Records" title="Proposal and agreement context" />
                <div className="mt-5 grid gap-4 md:grid-cols-4">
                  <RecordCard
                    label="Lead"
                    value={detail.lead?.fullName || "Unknown"}
                    helper={detail.lead?.email || "No email available"}
                    href={detail.lead?.id ? `/admin/leads/${detail.lead.id}` : "/admin/leads"}
                  />
                  <RecordCard
                    label="Client"
                    value={detail.proposal?.proposal.clientId ? "Linked" : "Not linked"}
                    helper={detail.proposal?.proposal.clientId ? "Open the linked client record" : "Open the client workspace"}
                    href={detail.proposal?.proposal.clientId ? `/admin/forms-client/${detail.proposal.proposal.clientId}` : "/admin/forms-client-directory"}
                  />
                  <RecordCard
                    label="Proposal"
                    value={detail.proposal?.proposal.proposalNumber || "Not linked"}
                    helper={detail.proposal?.proposal.packageName || "Open the proposal workspace"}
                    href="/admin/proposals#proposal-records"
                  />
                  <RecordCard
                    label="Agreement"
                    value={detail.agreement?.agreementNumber || "Not linked"}
                    helper={detail.agreement?.agreementUrl || "Open the agreement workspace"}
                    href={detail.agreement ? `/admin/agreement-records/${detail.agreement.id}` : "/admin/agreement-records"}
                  />
                </div>
              </article>
            </section>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function Hero({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
      <div className="relative mx-auto grid max-w-6xl gap-5">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
          <CreditCard className="size-4" aria-hidden="true" />
          {eyebrow}
        </div>
        <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
        <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">{description}</p>
      </div>
    </section>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CreditCard;
  label: string;
  value: number;
}) {
  return (
    <article className="command-center-card rounded-lg p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
          <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{typeof value === "number" && label.toLowerCase().includes("balance") || label.toLowerCase().includes("revenue") || label.toLowerCase().includes("deposit") ? formatMoney(value) : value}</p>
        </div>
        <span className="grid size-11 place-items-center rounded-lg bg-[#050505] text-[#FF6A2B]">
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </div>
    </article>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "Paid"
      ? "bg-emerald-500/18 text-emerald-200 border-emerald-400/30"
      : status === "Sent"
        ? "bg-[#FF6A2B]/12 text-[#FF6A2B] border-[#FF6A2B]/30"
        : status === "Partial"
          ? "bg-sky-500/18 text-sky-200 border-sky-400/30"
          : status === "Overdue"
            ? "bg-orange-500/18 text-orange-200 border-orange-400/30"
            : status === "Cancelled"
              ? "bg-rose-500/18 text-rose-200 border-rose-400/30"
              : "bg-white/8 text-[#fff9ef] border-[#FF6A2B]/18";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.14em] ${tone}`}>{status}</span>;
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{eyebrow}</p>
      <h3 className="mt-2 font-display text-2xl font-bold text-[#050505]">{title}</h3>
    </div>
  );
}

function DetailBlock({ title, items }: { title: string; items: Array<[string, string | undefined | null]> }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/16 bg-white/5 p-4">
      <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{title}</p>
      <dl className="mt-4 grid gap-3">
        {items.map(([label, value]) => (
          <div key={label} className="grid gap-1">
            <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#fff9ef]/58">{label}</dt>
            <dd className="break-words text-sm font-semibold leading-7 text-[#fff9ef]">{value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TimelineCard({ label, value, active }: { label: string; value: string; active: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${active ? "border-[#FF6A2B]/45 bg-[#FF6A2B]/10" : "border-[#D94A16]/16 bg-white/5"}`}>
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]">{value}</p>
    </div>
  );
}

function RecordCard({ label, value, helper, href }: { label: string; value: string; helper: string; href: string }) {
  return (
    <Link
      to={href}
      className="group grid gap-2 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B]/55 hover:bg-white/10"
    >
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="font-black text-[#fff9ef]">{value}</p>
      <p className="text-sm font-semibold leading-7 text-[#fff9ef]/68">{helper}</p>
      <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
        Open record
        <ArrowRight className="size-4 transition group-hover:translate-x-1" aria-hidden="true" />
      </span>
    </Link>
  );
}

function InfoCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/16 bg-white/5 p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]">{value || "—"}</p>
    </div>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[#D94A16]/24 bg-white/4 p-6 text-center">
      <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">{message}</p>
    </div>
  );
}

function Notice({ tone, message }: { tone: "error" | "success"; message: string }) {
  const classes =
    tone === "error"
      ? "border-red-300 bg-red-50 text-red-800"
      : "border-[#FF6A2B]/30 bg-white/5 text-[#fff9ef]";
  return <p className={`rounded-lg border px-5 py-4 text-sm font-bold ${classes}`}>{message}</p>;
}

function Th({ children }: { children: string }) {
  return <th className="px-4 py-4 align-top">{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-4 align-top text-sm font-semibold text-[#fff9ef]/72 ${className}`}>{children}</td>;
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function buildPaymentTimeline(detail: AdminPaymentDetail) {
  const reminder = detail.timeline.find((item) => item.activityType.toLowerCase().includes("reminder"));
  const depositReceived = detail.payment.amountPaidCents > 0 ? formatMoney(detail.payment.amountPaidCents) : "Not yet received";
  return [
    { label: "Invoice Created", value: formatDate(detail.payment.createdAt), active: Boolean(detail.payment.createdAt) },
    { label: "Invoice Sent", value: detail.payment.invoiceStatus || detail.payment.status, active: detail.payment.invoiceStatus === "Invoice Sent" || detail.payment.status === "Invoice Sent" },
    { label: "Deposit Received", value: depositReceived, active: detail.payment.amountPaidCents > 0 },
    { label: "Reminder Sent", value: reminder ? formatDate(reminder.createdAt) : "Not yet sent", active: Boolean(reminder) },
    { label: "Paid In Full", value: formatDate(detail.payment.paidAt), active: detail.payment.status === "Paid In Full" },
    { label: "Outstanding Balance", value: formatMoney(detail.payment.amountRemainingCents), active: detail.payment.amountRemainingCents > 0 },
  ];
}

function invoiceTitle(token: string) {
  return `INV-${token.slice(0, 8).toUpperCase()}`;
}

// Type-only helper to satisfy React.ReactNode without importing React.
type ReactNode = import("react").ReactNode;
